//
// Imports
//

// Types
import type { PendingSample } from "../../../services/api/domains/pending/pending.types";

/**
 * Edit Barcode Dialog Composable
 *
 * Handles editing sample barcodes in the validation workflow.
 *
 * @param key - Unique key for state isolation
 */
export const useEditBarcodeDialog = (key: string = "default") => {
  //
  // State
  //
  const isOpen = useState<boolean>(`editBarcode.${key}.isOpen`, () => false);
  const loading = useState<boolean>(`editBarcode.${key}.loading`, () => false);
  const newBarcode = useState<string>(`editBarcode.${key}.newBarcode`, () => "");
  const selectedSample = useState<PendingSample | null>(`editBarcode.${key}.selectedSample`, () => null);

  // Callback for save (to be set by parent)
  let saveCallback = async (): Promise<void> => {
    console.warn("[useEditBarcodeDialog] save() not set");
  };

  //
  // Computed Properties
  //
  const currentBarcode = computed(() => selectedSample.value?.barcode || "");
  const sampleType = computed(() => selectedSample.value?.typeName || "");

  // Validate barcode format
  const barcodeError = computed(() => {
    const barcode = newBarcode.value.trim();

    // Show error for empty input
    if (!barcode) {
      return "required";
    }

    // Check for quotes
    if (barcode.includes('"') || barcode.includes("'")) {
      return "containsQuotes"; // Translation key
    }

    // Check for backslashes
    if (barcode.includes("\\")) {
      return "containsBackslash";
    }

    // Check for special JSON characters
    if (barcode.includes("\n") || barcode.includes("\r") || barcode.includes("\t")) {
      return "containsSpecialChars";
    }

    // Optional: Check for valid barcode pattern (alphanumeric + dash/underscore)
    const validBarcodePattern = /^[A-Za-z0-9\-_]+$/;
    if (!validBarcodePattern.test(barcode)) {
      return "invalidCharacters";
    }

    return null;
  });

  const componentProps = computed(() => ({
    isOpen: isOpen.value,
    currentBarcode: currentBarcode.value,
    sampleType: sampleType.value,
    newBarcode: newBarcode.value,
    loading: loading.value,
    error: barcodeError.value,
    onBarcodeUpdate: (value: string) => {
      newBarcode.value = value;
    },
    onSave: () => saveCallback(),
    onCancel: close,
  }));

  //
  // Methods
  //

  /**
   * Open dialog with sample
   */
  const open = (sample: PendingSample): void => {
    selectedSample.value = sample;
    newBarcode.value = sample.barcode;
    isOpen.value = true;
  };

  /**
   * Close dialog and reset state
   */
  const close = (): void => {
    isOpen.value = false;
    // State is intentionally not cleared here — old data stays visible during
    // the close animation. State will be replaced by the next open() call.
  };

  // Setters for parent
  const setSave = (fn: () => Promise<void>) => {
    saveCallback = fn;
  };

  return {
    // State (readonly)
    isOpen: readonly(isOpen),
    loading,
    newBarcode,
    selectedSample: readonly(selectedSample),

    // Computed
    currentBarcode: readonly(currentBarcode),
    componentProps,

    // Methods
    open,
    close,

    // Setters for parent
    setSave,
  };
};
